import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    ArrowRightIcon,
    CheckBadgeIcon,
    BeakerIcon,
    CloudIcon,
    ChartBarIcon,
    UserGroupIcon,
    GlobeAltIcon,
    CpuChipIcon,
    SparklesIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import ContactPopup from '../../components/ContactPopup';
import SEO from '../../components/SEO';

const Home = () => {
    const [showContactPopup, setShowContactPopup] = useState(false);
    const [hasShownContactPopup, setHasShownContactPopup] = useState(false);
    const { user, setShowAuthPopup } = useAuth();

    useEffect(() => {
        const handleScroll = () => {
            // Show popup after scrolling down
            if (!hasShownContactPopup && window.scrollY > window.innerHeight * 2.5) {
                setShowContactPopup(true);
                setHasShownContactPopup(true);
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [hasShownContactPopup]);

    const stats = [
        { value: '50+', label: 'Expert Guidance' },
        { value: '100+', label: 'Completed Projects' },
        { value: '500+', label: 'Trusted by Farmers' },
        { value: '50+', label: 'Services' },
    ];

    const startupServices = [
        {
            title: "Precision Agriculture",
            description: "Utilizing technology to optimize farm productivity and enhance decision-making for sustainable agriculture."
        },
        {
            title: "Farm Management",
            description: "Integrated software solutions to streamline operations, increase efficiency, and support effective farm management."
        },
        {
            title: "Crop Protection",
            description: "Effective crop protection strategies for sustainable farming and optimized crop yields."
        },
        {
            title: "Expert Consultancy",
            description: "Our startup consultants have more than 20+ years of experience and completed more than 800 projects."
        }
    ];

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            <SEO
                title="Home - Marvelous Agri Solution | Best Agriculture App"
                description="Empowering farmers with AI-driven crop advisory, soil testing, and precision agriculture. Download the best agriculture app for maximum yield."
                keywords="Smart Farming, Agriculture App, Crop Advisory, Soil Testing, Precision Agriculture, Best Agri Solution, Kisan App"
            />
            {/* Hero Banner - Single Static Image */}
            <section className="relative h-[500px] md:h-[700px] overflow-hidden group">
                {/* Background Image - High Quality Agricultural Image */}
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-black/40 z-10" />
                    <img
                        src={require('../../assets/hero_background.jpg')}
                        alt="Marvelous Agri Solution Banner"
                        className="w-full h-full object-cover transform scale-105 hover:scale-110 transition-transform duration-[20s] ease-linear"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80' }}
                    />
                </div>

                {/* Content Container */}
                <div className="absolute inset-0 z-20 flex items-center justify-center md:justify-start">
                    <div className="container mx-auto px-6">
                        <motion.div
                            initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
                            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="max-w-3xl backdrop-blur-md bg-white/10 border border-white/20 p-8 md:p-12 rounded-3xl shadow-2xl overflow-hidden relative"
                        >
                            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />

                            <span className="inline-block px-4 py-1.5 bg-green-500/90 text-white rounded-full text-sm font-semibold tracking-wide mb-6 shadow-sm backdrop-blur-sm">
                                Next-Gen Agricultural Solutions
                            </span>
                            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
                                Empowering Farmers
                            </h1>
                            <p className="text-lg md:text-xl text-green-50 mb-8 leading-relaxed max-w-xl drop-shadow-md font-light">
                                Advanced technology for sustainable and profitable agriculture. We empower you with innovation and expertise.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link
                                    to="/services"
                                    className="px-8 py-4 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl transition-all duration-300 shadow-[0_4px_14px_0_rgba(22,163,74,0.39)] hover:shadow-[0_6px_20px_rgba(22,163,74,0.23)] hover:-translate-y-1 flex items-center justify-center gap-2"
                                >
                                    Our Services <ArrowRightIcon className="w-5 h-5" />
                                </Link>
                                <Link
                                    to="/contact"
                                    className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-all duration-300 backdrop-blur-sm border border-white/30 flex items-center justify-center"
                                >
                                    Contact Us
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Empowering Agriculture Section */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-6">
                    <div className="flex flex-col md:flex-row items-center gap-12">
                        <div className="md:w-1/2">
                            <img
                                src={require('../../assets/newimages/mas website about us.webp')}
                                alt="Modern Agriculture Consultant"
                                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
                            />
                        </div>
                        <div className="md:w-1/2">
                            <span className="text-green-600 font-bold tracking-widest uppercase text-sm mb-2 block">Our Mission</span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Empowering Agriculture Through Innovation</h2>
                            <p className="text-gray-600 mb-4 leading-relaxed">
                                Marvelous Agri Solution is a next-generation Agri-Tech company delivering integrated, technology-driven agricultural solutions — from precision farming and AI-powered advisory systems to advanced bio-inputs, smart irrigation, protected cultivation, and complete farm infrastructure development.
                            </p>
                            <p className="text-gray-600 mb-4 leading-relaxed">
                                We combine agronomy, digital intelligence, and sustainable expertise to build high-performance agricultural ecosystems that increase yield, optimize resources, and ensure long-term sustainability.
                            </p>
                            <p className="text-gray-600 mb-4 leading-relaxed">
                                From seed to harvest — and beyond to value addition and food processing — we design scalable farming systems that drive measurable growth and strengthen food security.
                            </p>
                            <p className="text-gray-600 mb-4 leading-relaxed">
                                We do not just supply products.<br />
                                We build intelligent agricultural success systems.
                            </p>
                            <p className="text-gray-600 mb-8 leading-relaxed font-semibold">
                                One Vision. One Platform. One Complete Agricultural Ecosystem.
                            </p>
                            <Link to="/about" className="inline-flex items-center text-green-700 font-bold hover:text-green-800 transition">
                                Read More <ArrowRightIcon className="w-5 h-5 ml-2" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Startup Services We Offer */}
            <section className="py-12 bg-green-900 text-white overflow-hidden relative">
                {/* Background Pattern */}
                <div className="absolute top-0 left-0 w-full h-full opacity-10">
                    <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <path d="M0 100 C 20 0 50 0 100 100 Z" fill="currentColor" />
                    </svg>
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-10 max-w-3xl mx-auto">
                        <span className="text-green-300 font-bold tracking-widest uppercase text-xs mb-2 block">What Makes Us Different</span>
                        <h2 className="text-2xl md:text-3xl font-bold mb-4 leading-tight">We Don’t Just Deliver Data. <br /> <span className="text-green-400">We Deliver Decisions.</span></h2>
                        <p className="text-base text-green-100 leading-relaxed">
                            At Marvelous Agri Solution, we transform satellite intelligence into field-level action. Most platforms show numbers. We convert numbers into clear guidance, measurable impact, and higher profitability.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Feature 1 */}
                        <div className="bg-white/10 backdrop-blur-md p-6 rounded-xl border border-white/20 hover:bg-white/20 transition duration-300 flex items-start gap-4">
                            <div className="w-10 h-10 bg-green-500 rounded-lg flex-shrink-0 flex items-center justify-center text-white">
                                <CpuChipIcon className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold mb-1">Precision + Practicality</h3>
                                <p className="text-sm text-green-100">Advanced satellite analytics meets real farmer challenges.</p>
                            </div>
                        </div>

                        {/* Feature 2 */}
                        <div className="bg-white/10 backdrop-blur-md p-6 rounded-xl border border-white/20 hover:bg-white/20 transition duration-300 flex items-start gap-4">
                            <div className="w-10 h-10 bg-green-500 rounded-lg flex-shrink-0 flex items-center justify-center text-white">
                                <ChartBarIcon className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold mb-1">Insight → Action → Impact</h3>
                                <p className="text-sm text-green-100">We don’t stop at reporting. We guide execution at every stage of crop growth.</p>
                            </div>
                        </div>

                        {/* Feature 3 */}
                        <div className="bg-white/10 backdrop-blur-md p-6 rounded-xl border border-white/20 hover:bg-white/20 transition duration-300 flex items-start gap-4">
                            <div className="w-10 h-10 bg-green-500 rounded-lg flex-shrink-0 flex items-center justify-center text-white">
                                <UserGroupIcon className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold mb-1">Partnership, Not Platform</h3>
                                <p className="text-sm text-green-100">We work with farmers — not just for them.</p>
                            </div>
                        </div>

                        {/* Feature 4 */}
                        <div className="bg-white/10 backdrop-blur-md p-6 rounded-xl border border-white/20 hover:bg-white/20 transition duration-300 flex items-start gap-4">
                            <div className="w-10 h-10 bg-green-500 rounded-lg flex-shrink-0 flex items-center justify-center text-white">
                                <BeakerIcon className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold mb-1">Innovation Rooted in Soil Reality</h3>
                                <p className="text-sm text-green-100">Technology designed for real fields, real risks, and real livelihoods.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Commitment Section */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-12">
                        <span className="text-green-600 font-bold tracking-widest uppercase text-sm mb-2 block">OUR COMMITMENT TO FARMERS</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Your Growth Is Our Mission.</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
                        <div className="bg-green-50 p-8 rounded-2xl shadow-sm border border-green-100 h-full">
                            <h3 className="text-xl font-bold text-green-800 mb-6 flex items-center gap-2">
                                <SparklesIcon className="w-6 h-6" /> We believe farmers deserve:
                            </h3>
                            <ul className="space-y-4">
                                {[
                                    "Accurate information",
                                    "Transparent recommendations",
                                    "Profitable outcomes",
                                    "Sustainable farming solutions"
                                ].map((item, idx) => (
                                    <li key={idx} className="flex items-center gap-3 text-gray-700 font-medium">
                                        <CheckBadgeIcon className="w-6 h-6 text-green-600 flex-shrink-0" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="flex flex-col justify-center h-full space-y-6">
                            <div className="text-center md:text-left">
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">Our commitment is simple yet powerful:</h3>
                                <div className="space-y-3 text-lg text-gray-600">
                                    <p className="flex items-center gap-2 justify-center md:justify-start"><span className="w-2 h-2 bg-green-500 rounded-full"></span> Empower every acre with intelligence.</p>
                                    <p className="flex items-center gap-2 justify-center md:justify-start"><span className="w-2 h-2 bg-green-500 rounded-full"></span> Protect every crop with precision.</p>
                                    <p className="flex items-center gap-2 justify-center md:justify-start"><span className="w-2 h-2 bg-green-500 rounded-full"></span> Increase every farmer’s confidence.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="max-w-4xl mx-auto bg-gray-900 text-white rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-green-600/20 rounded-full blur-3xl -mr-32 -mt-32"></div>
                        <div className="absolute bottom-0 left-0 w-64 h-64 bg-green-600/20 rounded-full blur-3xl -ml-32 -mb-32"></div>

                        <div className="relative z-10 text-center">
                            <h3 className="text-2xl font-bold mb-8">We are committed to:</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left mb-10">
                                {[
                                    "Making advanced technology simple and accessible",
                                    "Reducing input costs through smart monitoring",
                                    "Increasing yield through data-driven advisory",
                                    "Supporting sustainable and climate-resilient farming"
                                ].map((item, idx) => (
                                    <div key={idx} className="flex items-start gap-3">
                                        <div className="mt-1 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                                            <CheckBadgeIcon className="w-3 h-3 text-white" />
                                        </div>
                                        <span className="text-green-50 font-medium">{item}</span>
                                    </div>
                                ))}
                            </div>
                            <blockquote className="text-xl md:text-2xl font-serif italic text-green-200 border-t border-white/10 pt-8">
                                "Because agriculture is not just a business. <br className="hidden md:block" />
                                It is livelihood, legacy, and leadership."
                            </blockquote>
                        </div>
                    </div>
                </div>
            </section>

            {/* Startup Services We Offer */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-12">
                        <span className="text-green-600 font-bold tracking-widest uppercase text-sm">What We Do</span>
                        <h2 className="text-3xl font-bold text-gray-900 mt-2">Startup Services We Offer</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {startupServices.map((service, idx) => (
                            <div key={idx} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100 group">
                                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-6 text-green-600 group-hover:bg-green-600 group-hover:text-white transition-colors">
                                    <SparklesIcon className="w-8 h-8" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                                <p className="text-gray-600 leading-relaxed text-sm">{service.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Future of Farming */}
            <section className="py-16 bg-white relative overflow-hidden">
                <div className="absolute inset-0 bg-green-900 opacity-90 z-0"></div>
                <img src={require('../../assets/newimages/mas website resarch.webp')} alt="Smart Farming Drone" className="absolute inset-0 w-full h-full object-cover mix-blend-overlay z-0" />

                <div className="container mx-auto px-6 relative z-10 text-center">
                    <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">The Future of Farming with Marvelous Agri Solution</h2>
                    <p className="text-green-100 max-w-3xl mx-auto mb-10 text-lg leading-relaxed">
                        We empower farmers with satellite-based consultancy and biological and botanical products for improving soil texture and structure, or soil fertility also for plant root development.
                    </p>
                    <Link to="/contact" className="px-10 py-4 bg-white text-green-800 font-bold rounded-full hover:bg-green-50 transition shadow-lg inline-block">
                        Get Started Today
                    </Link>
                </div>
            </section>


            {/* Stats Section */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {stats.map((stat, idx) => (
                            <div key={idx} className="text-center p-6 bg-gray-50 rounded-2xl hover:bg-green-50 transition duration-300">
                                <div className="text-4xl md:text-5xl font-extrabold text-green-600 mb-2">{stat.value}</div>
                                <div className="text-gray-700 font-medium">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Core Solutions Snapshot */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-12">
                        <span className="text-green-600 font-bold tracking-widest uppercase text-sm">Our Expertise</span>
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2">Comprehensive Solutions</h2>
                        <p className="text-gray-600 max-w-2xl mx-auto mt-2 text-sm">
                            Explore innovative solutions for modern agriculture through our expert consultancy and advanced technology.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                            { title: "Smart Farming", icon: <CpuChipIcon className="w-6 h-6" /> },
                            { title: "Crop Advisory", icon: <ChartBarIcon className="w-6 h-6" /> },
                            { title: "Soil Health", icon: <BeakerIcon className="w-6 h-6" /> },
                            { title: "Pest Management", icon: <CheckBadgeIcon className="w-6 h-6" /> },
                            { title: "Irrigation", icon: <CloudIcon className="w-6 h-6" /> },
                            { title: "Organic Farming", icon: <GlobeAltIcon className="w-6 h-6" /> },
                            { title: "Hydroponics", icon: <UserGroupIcon className="w-6 h-6" /> },
                            { title: "Post-Harvest", icon: <ArrowRightIcon className="w-6 h-6" /> },
                        ].map((item, idx) => (
                            <Link
                                key={idx}
                                to="/services"
                                className="bg-white p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow flex items-center gap-3 border border-gray-100"
                            >
                                <div className="p-2 bg-green-50 rounded-lg text-green-600">
                                    {item.icon}
                                </div>
                                <h3 className="font-medium text-gray-800 text-sm">{item.title}</h3>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-6">
                    <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-8 md:p-12 text-white text-center">
                        <SparklesIcon className="w-12 h-12 mx-auto mb-4 text-green-200" />
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">Let's Grow Together</h2>
                        <p className="text-green-100 mb-8 max-w-xl mx-auto text-lg">
                            Whether you are a farmer, partner, or institution — Marvelous Agri Solution is your growth partner.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/contact" className="px-8 py-4 bg-white text-green-700 font-bold rounded-xl hover:bg-gray-100 transition shadow-lg">
                                Contact Us Today
                            </Link>
                            <Link to="/app" className="px-8 py-4 bg-green-800 text-white font-bold rounded-xl hover:bg-green-900 transition border-2 border-green-500">
                                Download Our App
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <ContactPopup isOpen={showContactPopup} onClose={() => setShowContactPopup(false)} />
        </div>
    );
};

export default Home;
